import React, { useState, useEffect } from 'react';

import { X } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import AnswerEditorSimple from './components/answer-editor-simple';
import AnswerPreview from './components/answer-preview';
import CitationAssigner from './components/citation-assigner';
import CitationList from './components/citation-list';
import ModalFooter from './components/modal-footer';
import QuestionInput from './components/question-input';
import { useCreateManualThread } from '@/api/mutations/useCreateManualThread';
import type { ReferencesDict } from '@/api/mutations/useCreateManualThread';
import { useUpdateManualThread } from '@/api/mutations/useUpdateManualThread';
import { useGetChunkV2 } from '@/api/queries/useGetChunkV2';
import { useGetFact } from '@/api/queries/useGetFact';

export interface EditModeData {
  threadId: string;
  question: string;
  answer: string;
  references: ReferencesDict;
}

interface ManualAnswerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (threadId: string) => void;
  editMode?: EditModeData;
}

const ManualAnswerModal: React.FC<ManualAnswerModalProps> = ({ isOpen, onClose, onComplete, editMode }) => {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) return null;

  const isEditMode = !!editMode;

  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [references, setReferences] = useState<ReferencesDict>({});
  const [selectedCitation, setSelectedCitation] = useState<string | null>(null);
  const [citationNumbers, setCitationNumbers] = useState<string[]>([]);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const { mutate: createThread, isLoading: isCreating } = useCreateManualThread();
  const { mutate: updateThread, isLoading: isUpdating } = useUpdateManualThread();

  // Initialize state from edit mode data when modal opens
  useEffect(() => {
    if (isOpen && editMode && !isInitialized) {
      setQuestion(editMode.question);
      setAnswer(editMode.answer);
      setReferences(editMode.references);
      setIsInitialized(true);
    }
    // Reset initialization flag when modal closes
    if (!isOpen) {
      setIsInitialized(false);
    }
  }, [isOpen, editMode, isInitialized]);

  // Get the current reference details
  const currentRef = selectedCitation ? references[selectedCitation] : null;
  const { data: currentFact } = useGetFact(matterId, currentRef?.type === 'fact' ? currentRef.id : '');
  const { data: currentChunk } = useGetChunkV2(matterId, currentRef?.type === 'chunk' ? currentRef.id : null);

  // Extract citation numbers from answer
  useEffect(() => {
    const regex = /\[(\d+)\]/g;
    const numbers = new Set<string>();
    let match;
    while ((match = regex.exec(answer)) !== null) {
      numbers.add(match[1]);
    }
    const sorted = Array.from(numbers).sort((a, b) => parseInt(a) - parseInt(b));
    setCitationNumbers(sorted);

    // Auto-select first unassigned citation
    if (selectedCitation === null || !sorted.includes(selectedCitation)) {
      const firstUnassigned = sorted.find((n) => !references[n]);
      setSelectedCitation(firstUnassigned || (sorted.length > 0 ? sorted[0] : null));
    }
  }, [answer, references, selectedCitation]);

  const handleAssignReference = (id: string, type: 'fact' | 'chunk') => {
    if (!selectedCitation) return;

    // Check if this fact/chunk is already assigned to another citation
    const existingCitation = Object.entries(references).find(
      ([citationNum, ref]) => ref.id === id && citationNum !== selectedCitation,
    );

    if (existingCitation) {
      toast.error(`This ${type} is already referenced by citation [${existingCitation[0]}]`);
      return;
    }

    const updatedRefs = {
      ...references,
      [selectedCitation]: { id, type },
    };
    setReferences(updatedRefs);
  };

  const handleRemoveReference = (citationNum: string) => {
    const updatedRefs = { ...references };
    delete updatedRefs[citationNum];
    setReferences(updatedRefs);
  };

  const handleSave = () => {
    if (!question.trim()) {
      toast.error('Please enter a question');
      return;
    }
    if (!answer.trim()) {
      toast.error('Please enter an answer');
      return;
    }

    // Filter references to only include citations that exist in the answer
    // This removes orphaned references when citations are deleted from the answer text
    const filteredReferences: ReferencesDict = {};
    for (const citationNum of citationNumbers) {
      if (references[citationNum]) {
        filteredReferences[citationNum] = references[citationNum];
      }
    }

    // Check for unassigned citations
    const assignedCount = Object.keys(filteredReferences).length;
    const totalCount = citationNumbers.length;
    if (assignedCount < totalCount) {
      toast.error('All citations must be assigned to a fact or chunk before saving');
      return;
    }

    const onSuccess = (data: { threadId: string }) => {
      toast.success(isEditMode ? 'Manual answer updated successfully' : 'Manual answer saved successfully');
      // Clear all state
      setQuestion('');
      setAnswer('');
      setReferences({});
      setSelectedCitation(null);
      setCitationNumbers([]);
      setIsPreviewMode(false);
      onComplete(data.threadId);
      onClose();
    };

    const onError = (error: Error) => {
      toast.error(error.message || `Failed to ${isEditMode ? 'update' : 'save'} manual answer`);
    };

    if (isEditMode && editMode) {
      // Update existing thread
      updateThread(
        {
          matterId,
          subsetId,
          threadId: editMode.threadId,
          question,
          answer,
          references: filteredReferences,
        },
        { onSuccess, onError },
      );
    } else {
      // Create new thread
      createThread(
        {
          matterId,
          subsetId,
          question,
          answer,
          references: filteredReferences,
        },
        { onSuccess, onError },
      );
    }
  };

  const handleClose = () => {
    if (question.trim() || answer.trim()) {
      if (confirm('You have unsaved changes. Are you sure you want to close?')) {
        // Clear all state
        setQuestion('');
        setAnswer('');
        setReferences({});
        setSelectedCitation(null);
        setCitationNumbers([]);
        setIsPreviewMode(false);
        onClose();
      }
    } else {
      // Clear all state even if no changes
      setQuestion('');
      setAnswer('');
      setReferences({});
      setSelectedCitation(null);
      setCitationNumbers([]);
      setIsPreviewMode(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  const assignedCount = Object.keys(references).length;
  const totalCount = citationNumbers.length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="flex h-[90vh] w-[95vw] flex-col overflow-hidden rounded-lg bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 className="text-xl font-semibold text-gray-800">
            {isEditMode ? 'Edit Manual Answer' : 'Create Manual Answer'}
          </h2>
          <button onClick={handleClose} className="text-gray-400 transition-colors hover:text-gray-600">
            <X className="size-6" />
          </button>
        </div>

        {/* Main Content */}
        <div className="flex flex-1 overflow-hidden">
          <div className="flex w-[50%] flex-col border-r border-gray-200 p-6">
            <QuestionInput question={question} onChange={setQuestion} />

            <div className="mt-4 flex grow flex-col overflow-hidden">
              <div className="mb-3 flex gap-1 self-start rounded-lg bg-gray-100 p-1">
                <button
                  onClick={() => setIsPreviewMode(false)}
                  className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                    !isPreviewMode ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Edit
                </button>
                <button
                  onClick={() => setIsPreviewMode(true)}
                  className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                    isPreviewMode ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Preview
                </button>
              </div>

              <div className="grow overflow-scroll">
                {isPreviewMode ? (
                  <AnswerPreview answer={answer} references={references} />
                ) : (
                  <AnswerEditorSimple
                    answer={answer}
                    onChange={setAnswer}
                    onInsertCitation={(marker) => {
                      setAnswer((prev) => prev + marker);
                    }}
                    citationNumbers={citationNumbers}
                  />
                )}
              </div>
            </div>
          </div>

          <div className="flex w-[50%] flex-col overflow-hidden p-6">
            <div className="mb-4 flex-shrink-0">
              <CitationList
                citationNumbers={citationNumbers}
                references={references}
                selectedCitation={selectedCitation}
                onSelectCitation={setSelectedCitation}
                onRemoveReference={handleRemoveReference}
              />
            </div>

            {selectedCitation && references[selectedCitation] && (currentFact || currentChunk) && (
              <div className="mb-4 flex-shrink-0 rounded-md bg-green-100 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-semibold text-green-800">Citation [{selectedCitation}]</span>
                  <span className="text-xs font-medium text-green-700">
                    {references[selectedCitation].type === 'fact' ? 'Fact' : 'Chunk'}
                  </span>
                </div>
                <div className="max-h-32 overflow-y-auto rounded bg-white p-2 text-xs text-gray-800">
                  {currentFact ? currentFact.description : currentChunk?.content}
                </div>
              </div>
            )}

            <div className="flex min-h-0 flex-1 flex-col border-t border-gray-200 pt-4">
              {selectedCitation && (
                <CitationAssigner
                  caseId={subsetId}
                  selectedCitation={selectedCitation}
                  onAssign={handleAssignReference}
                  currentlyAssignedId={currentRef?.id}
                  currentlyAssignedType={currentRef?.type}
                />
              )}
            </div>
          </div>
        </div>

        <ModalFooter
          onCancel={handleClose}
          onSave={handleSave}
          isLoading={isCreating || isUpdating}
          hasUnassignedCitations={assignedCount < totalCount}
        />
      </div>
    </div>
  );
};

export default ManualAnswerModal;
